import React, { useState } from "react";
import styled from "styled-components";
import axios from "axios";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    width: 95%;
    padding: 1.5rem;
    border-radius: 12px;
    max-height: 95vh;
  }

  @media (max-width: 480px) {
    width: 98%;
    padding: 1rem;
    border-radius: 8px;
    margin: 0.5rem;
  }
`;

const ModalTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 2rem;
  text-align: center;
  font-family: "Cairo", sans-serif;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }
`;

const CalendarContainer = styled.div`
  margin-bottom: 2rem;
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const MonthYear = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
`;

const NavButton = styled.button`
  background: #3498db;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background 0.3s ease;

  &:hover {
    background: #2980b9;
  }

  &:disabled {
    background: #bdc3c7;
    cursor: not-allowed;
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.5rem;
  margin-bottom: 1rem;

  @media (max-width: 480px) {
    gap: 0.25rem;
  }
`;

const DayHeader = styled.div`
  text-align: center;
  font-weight: 600;
  color: #7f8c8d;
  padding: 0.5rem;
  font-size: 0.9rem;
`;

const DayCell = styled.button<{ isAvailable: boolean; isSelected: boolean }>`
  padding: 0.8rem;
  border: 2px solid ${(props) => (props.isSelected ? "#3498db" : "#e0e0e0")};
  background: ${(props) =>
    props.isSelected ? "#3498db" : props.isAvailable ? "white" : "#f8f9fa"};
  color: ${(props) =>
    props.isSelected ? "white" : props.isAvailable ? "#2c3e50" : "#bdc3c7"};
  border-radius: 8px;
  cursor: ${(props) => (props.isAvailable ? "pointer" : "not-allowed")};
  font-weight: 500;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 0.6rem;
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    padding: 0.4rem;
    font-size: 0.8rem;
    min-height: 40px;
  }

  &:hover {
    ${(props) =>
      props.isAvailable &&
      !props.isSelected &&
      `
      border-color: #3498db;
      background: #f8f9fa;
    `}
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const TimeSlotContainer = styled.div`
  margin-bottom: 2rem;
`;

const TimeSlotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const TimeSlot = styled.button<{ isSelected: boolean }>`
  padding: 0.8rem;
  border: 2px solid ${(props) => (props.isSelected ? "#3498db" : "#e0e0e0")};
  background: ${(props) => (props.isSelected ? "#3498db" : "white")};
  color: ${(props) => (props.isSelected ? "white" : "#2c3e50")};
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    ${(props) =>
      !props.isSelected &&
      `
      border-color: #3498db;
      background: #f8f9fa;
    `}
  }

  @media (max-width: 768px) {
    padding: 0.7rem;
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    padding: 0.6rem;
    font-size: 0.85rem;
    width: 100%;
  }
`;

const FormContainer = styled.div`
  margin-top: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #2c3e50;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #3498db;
  }

  @media (max-width: 768px) {
    padding: 0.7rem;
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    padding: 0.6rem;
    font-size: 0.9rem;
  }
`;

const InputGroup = styled.div`
  margin-bottom: 1.5rem;
  font-family: Cairo;
  direction: rtl;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.8rem;
  direction: rtl;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #3498db;
  }

  @media (max-width: 768px) {
    padding: 0.7rem;
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    padding: 0.6rem;
    font-size: 0.9rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

const Button = styled.button<{ variant?: "primary" | "secondary" }>`
  padding: 0.8rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: "Cairo", sans-serif;

  ${(props) =>
    props.variant === "primary"
      ? `
    background: linear-gradient(135deg, #3498db, #2980b9);
    color: white;
    
    &:hover {
      background: linear-gradient(135deg, #2980b9, #21618c);
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(52, 152, 219, 0.3);
    }
  `
      : `
    background: #ecf0f1;
    color: #2c3e50;
    
    &:hover {
      background: #d5dbdb;
    }
  `}

  &:disabled {
    background: #bdc3c7;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 0.7rem 1.25rem;
    font-size: 0.95rem;
    width: 100%;
  }

  @media (max-width: 480px) {
    padding: 0.6rem 1rem;
    font-size: 0.9rem;
  }
`;

interface BookingData {
  name: string;
  age: string;
  phone: string;
  email: string;
  nationality: string;
  studyDestination: string;
  previousIELTS: string;
  englishLevel: string;
  fieldOfStudy: string;
  date: string;
  time: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  language: "ar" | "en";
}

const IeltsCalendarModal: React.FC<Props> = ({ isOpen, onClose, language }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [step, setStep] = useState<"calendar" | "form">("calendar");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<BookingData>({
    name: "",
    age: "",
    phone: "",
    email: "",
    nationality: "",
    studyDestination: "",
    previousIELTS: "",
    englishLevel: "",
    fieldOfStudy: "",
    date: "",
    time: "",
  });

  const content = {
    ar: {
      title: "حجز موعد اختبار الآيلتس",
      selectDate: "اختر التاريخ",
      selectTime: "اختر الوقت",
      personalInfo: "المعلومات الشخصية",
      name: "الاسم؟",
      age: "العمر؟",
      phone: "رقم الموبايل؟",
      email: "الايميل؟",
      nationality: "الجنسية؟",
      studyDestination: "اريد الدراسة في...",
      uk: "بريطانيا",
      otherCountry: "غيرها",
      previousIELTS: "هل اخذت اختبار الآيلتس من قبل؟",
      yes: "نعم",
      no: "لا",
      englishLevel: "كيف تقيم مستواك باللغة الانجليزية؟",
      weak: "ضعيف",
      average: "متوسط",
      excellent: "ممتاز",
      fieldOfStudy: "ماذا تريد ان تدرس كتخصص؟",
      back: "رجوع",
      next: "التالي",
      book: "احجز الآن",
      cancel: "إلغاء",
    },
    en: {
      title: "حجز موعد اختبار الآيلتس",
      selectDate: "اختر التاريخ",
      selectTime: "اختر الوقت",
      personalInfo: "المعلومات الشخصية",
      name: "الاسم؟",
      age: "العمر؟",
      phone: "رقم الموبايل؟",
      email: "الايميل؟",
      nationality: "الجنسية؟",
      studyDestination: "اريد الدراسة في...",
      uk: "بريطانيا",
      otherCountry: "غيرها",
      previousIELTS: "هل اخذت اختبار الآيلتس من قبل؟",
      yes: "نعم",
      no: "لا",
      englishLevel: "كيف تقيم مستواك باللغة الانجليزية؟",
      weak: "ضعيف",
      average: "متوسط",
      excellent: "ممتاز",
      fieldOfStudy: "ماذا تريد ان تدرس كتخصص؟",
      back: "رجوع",
      next: "التالي",
      book: "احجز الآن",
      cancel: "إلغاء",
    },
  };

  const currentContent = content[language];

  // Generate time slots - only 4-6pm slot
  const generateTimeSlots = () => {
    return ["4:00 PM - 6:00 PM"];
  };

  const timeSlots = generateTimeSlots();

  // Check if date is available - only October 15th, 2025
  const isDateAvailable = (date: Date) => {
    const targetDate = new Date(2025, 10, 19); // October is month 9 (0-indexed)
    return (
      date.getFullYear() === targetDate.getFullYear() &&
      date.getMonth() === targetDate.getMonth() &&
      date.getDate() === targetDate.getDate()
    );
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const handleDateSelect = (date: Date) => {
    if (isDateAvailable(date)) {
      setSelectedDate(date);
      setSelectedTime("");
    }
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
  };

  const handleNext = () => {
    if (selectedDate && selectedTime) {
      setFormData({
        ...formData,
        date: selectedDate.toISOString().split("T")[0],
        time: selectedTime,
      });
      setStep("form");
    }
  };

  const handleBack = () => {
    setStep("calendar");
  };

  const handleInputChange = (field: keyof BookingData, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Submit to backend API
      const response = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT}/ielts-registration`,
        {
          name: formData.name,
          age: formData.age,
          phoneNumber: formData.phone,
          email: formData.email,
          nationality: formData.nationality,
          studyDestination: formData.studyDestination,
          previousIELTS: formData.previousIELTS,
          englishLevel: formData.englishLevel,
          fieldOfStudy: formData.fieldOfStudy,
          scholarship: "No", // Default value, can be made dynamic
          registrationTime: formData.time,
          guests: [],
          // Calendar specific data (can be stored as additional info)
          bookingDate: formData.date,
          bookingTime: formData.time,
        }
      );

      if (response.data.success) {
        alert(
          `Booking confirmed successfully! Your confirmation code is: ${response.data.data.registration.confirmationCode}`
        );
        onClose();
        // Reset form
        setFormData({
          name: "",
          age: "",
          phone: "",
          email: "",
          nationality: "",
          studyDestination: "",
          previousIELTS: "",
          englishLevel: "",
          fieldOfStudy: "",
          date: "",
          time: "",
        });
        setSelectedDate(null);
        setSelectedTime("");
        setStep("calendar");
      }
    } catch (error: any) {
      console.error("Error submitting booking:", error);
      if (error.response?.data?.message) {
        alert(`Error: ${error.response.data.message}`);
      } else {
        alert("Error submitting booking. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const calendarDays = generateCalendarDays();
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return (
    <ModalOverlay onClick={handleBackdropClick}>
      <ModalContent>
        <ModalTitle>حجز موعد ورشة الآيلتس</ModalTitle>

        {step === "calendar" && (
          <>
            <CalendarContainer>
              <CalendarHeader>
                <NavButton
                  onClick={() =>
                    setCurrentDate(
                      new Date(
                        currentDate.getFullYear(),
                        currentDate.getMonth() - 1
                      )
                    )
                  }
                >
                  ←
                </NavButton>
                <MonthYear>
                  {monthNames[currentDate.getMonth()]}{" "}
                  {currentDate.getFullYear()}
                </MonthYear>
                <NavButton
                  onClick={() =>
                    setCurrentDate(
                      new Date(
                        currentDate.getFullYear(),
                        currentDate.getMonth() + 1
                      )
                    )
                  }
                >
                  →
                </NavButton>
              </CalendarHeader>

              <CalendarGrid>
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                  (day) => (
                    <DayHeader key={day}>{day}</DayHeader>
                  )
                )}
                {calendarDays.map((date, index) => (
                  <DayCell
                    key={index}
                    isAvailable={isDateAvailable(date)}
                    isSelected={
                      selectedDate?.toDateString() === date.toDateString()
                    }
                    onClick={() => handleDateSelect(date)}
                    disabled={!isDateAvailable(date)}
                  >
                    {date.getDate()}
                  </DayCell>
                ))}
              </CalendarGrid>
            </CalendarContainer>

            {selectedDate && (
              <TimeSlotContainer>
                <h4>{currentContent.selectTime}</h4>
                <TimeSlotGrid>
                  {timeSlots.map((time) => (
                    <TimeSlot
                      key={time}
                      isSelected={selectedTime === time}
                      onClick={() => handleTimeSelect(time)}
                    >
                      {time}
                    </TimeSlot>
                  ))}
                </TimeSlotGrid>
              </TimeSlotContainer>
            )}

            <ButtonContainer>
              <Button variant="secondary" onClick={onClose}>
                {currentContent.cancel}
              </Button>
              <Button
                variant="primary"
                onClick={handleNext}
                disabled={!selectedDate || !selectedTime}
              >
                {currentContent.next}
              </Button>
            </ButtonContainer>
          </>
        )}

        {step === "form" && (
          <FormContainer>
            <h3 style={{ fontFamily: "Cairo", direction: "rtl" }}>
              {currentContent.personalInfo}
            </h3>

            <InputGroup>
              <Label>{currentContent.name}</Label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder={"Name"}
              />
            </InputGroup>

            <InputGroup>
              <Label>{currentContent.age}</Label>
              <Input
                type="text"
                value={formData.age}
                onChange={(e) =>
                  setFormData({ ...formData, age: e.target.value })
                }
                placeholder={"Age"}
              />
            </InputGroup>

            <InputGroup>
              <Label>{currentContent.phone}</Label>
              <Input
                type="tel"
                value={formData.phone}
                style={{ direction: "rtl" }}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                placeholder={"Phone Number"}
              />
            </InputGroup>

            <InputGroup>
              <Label>{currentContent.email}</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder={"Email"}
              />
            </InputGroup>

            <InputGroup>
              <Label>{currentContent.nationality}</Label>
              <Input
                type="text"
                value={formData.nationality}
                onChange={(e) =>
                  setFormData({ ...formData, nationality: e.target.value })
                }
                placeholder={"Nationality"}
              />
            </InputGroup>

            <InputGroup>
              <Label>{currentContent.studyDestination}</Label>
              <select
                value={formData.studyDestination}
                onChange={(e) =>
                  setFormData({ ...formData, studyDestination: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  backgroundColor: "white",
                }}
              >
                <option value="">{currentContent.studyDestination}</option>
                <option value="uk">{currentContent.uk}</option>
                <option value="other">{currentContent.otherCountry}</option>
              </select>
            </InputGroup>

            <InputGroup>
              <Label>{currentContent.previousIELTS}</Label>
              <select
                value={formData.previousIELTS}
                onChange={(e) =>
                  setFormData({ ...formData, previousIELTS: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  backgroundColor: "white",
                }}
              >
                <option value="">{currentContent.previousIELTS}</option>
                <option value="yes">{currentContent.yes}</option>
                <option value="no">{currentContent.no}</option>
              </select>
            </InputGroup>

            <InputGroup>
              <Label>{currentContent.englishLevel}</Label>
              <select
                value={formData.englishLevel}
                onChange={(e) =>
                  setFormData({ ...formData, englishLevel: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  backgroundColor: "white",
                }}
              >
                <option value="">{currentContent.englishLevel}</option>
                <option value="weak">{currentContent.weak}</option>
                <option value="average">{currentContent.average}</option>
                <option value="excellent">{currentContent.excellent}</option>
              </select>
            </InputGroup>

            <InputGroup>
              <Label>{currentContent.fieldOfStudy}</Label>
              <Input
                type="text"
                value={formData.fieldOfStudy}
                onChange={(e) =>
                  setFormData({ ...formData, fieldOfStudy: e.target.value })
                }
                placeholder={"Major"}
              />
            </InputGroup>

            <ButtonContainer>
              <Button variant="secondary" onClick={handleBack}>
                {currentContent.back}
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={
                  !formData.name ||
                  !formData.age ||
                  !formData.phone ||
                  !formData.email ||
                  !formData.nationality ||
                  !formData.studyDestination ||
                  !formData.previousIELTS ||
                  !formData.englishLevel ||
                  !formData.fieldOfStudy ||
                  isSubmitting
                }
              >
                {isSubmitting ? "Booking..." : currentContent.book}
              </Button>
            </ButtonContainer>
          </FormContainer>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default IeltsCalendarModal;
